<template>
  <div class="customer-inout">
    <Navi :id="customer.id" :isSP="isSP" @save="saveCustomer" @cancel="cancelEditCustomer" editing />
    <div class="customer__book">
      <ImageMemo v-if="allImageMemos.length" deletable @delete="deleteCustomerImage" />
      <dl class="cstm-note">
        <dt>ふりがな</dt>
        <dd>
          <div class="input-text-wrap">
            <TextInput class="wd--half" v-model="lastNameKana" />
            <TextInput class="wd--half" v-model="firstNameKana" />
          </div>
        </dd>
        <dt>お名前</dt>
        <dd>
          <div class="input-text-wrap">
            <TextInput class="wd--half" v-model="lastName" />
            <TextInput class="wd--half" v-model="firstName" />
          </div>
        </dd>
        <dt>電話番号1</dt>
        <dd>
          <NumericInput class="wd--half" v-model="tel1" :overWrite="false" />
        </dd>
        <dt>電話番号2</dt>
        <dd>
          <NumericInput class="wd--half" v-model="tel2" :overWrite="false" />
        </dd>
        <dt>メール1</dt>
        <dd><TextInput v-model="mail1" /></dd>
        <dt>メール2</dt>
        <dd><TextInput v-model="mail2" /></dd>
      </dl>
      <dl class="cstm-note">
        <dt>会社名</dt>
        <dd><TextInput v-model="officeName" /></dd>
        <dt>部署</dt>
        <dd><TextInput v-model="department" /></dd>
        <dt>役職名</dt>
        <dd><TextInput v-model="post" /></dd>
        <dt>会社郵便番号</dt>
        <dd>
          <NumericInput class="wd--half" :formatter="format" v-model="officeZipCode" :overWrite="false" />
        </dd>
        <dt>会社住所</dt>
        <dd><TextInput v-model="officeAddress" /></dd>
        <dt>会社電話番号</dt>
        <dd>
          <NumericInput class="wd--half" v-model="officeTel" :overWrite="false" />
        </dd>
        <dt>領収書宛名</dt>
        <dd><TextInput v-model="receiptAddress" /></dd>
        <dt>勤務先メモ</dt>
        <dd><TextInput v-model="officeRemarks" /></dd>
      </dl>
      <dl class="cstm-note">
        <dt>自宅郵便番号</dt>
        <dd>
          <NumericInput class="wd--half" :formatter="format" v-model="zipCode" :overWrite="false" />
        </dd>
        <dt>自宅住所</dt>
        <dd><TextInput v-model="address" /></dd>
      </dl>
      <dl class="cstm-note">
        <dt>お客様特徴</dt>
        <dd class="tb-customer-attributes">
          <ul class="checkbox-list">
            <li v-for="(attribute, key) in attributesMap" :key="key">
              <CheckBox :value="+key" v-model="attributes">{{ attribute.label }}</CheckBox>
            </li>
          </ul>
          <TagInput
            class="other-attributes"
            v-model="otherAttributes"
            :tags="allOtherAttributes"
            label="その他を選択"
          />
        </dd>
        <dd class="sp-customer-attributes">
          <ToggleButton
            v-for="(attr, key) in attributesMap"
            :key="`attr_${key}`"
            class="attr"
            :value="+key"
            v-model="attributes"
          >
            {{ attr.label }}
          </ToggleButton>
          <ToggleButton
            v-for="(attr, key) in allOtherAttributes"
            :key="`other_attr_${key}`"
            class="attr"
            :value="attr"
            v-model="otherAttributes"
          >
            {{ attr.name }}
          </ToggleButton>
        </dd>
        <dt>性別</dt>
        <dd>
          <RadioList :items="genderMap" v-model.number="gender" />
        </dd>
        <dt>誕生日</dt>
        <dd><DateInput v-model="birthday" /></dd>
        <dt>結婚記念日</dt>
        <dd><DateInput v-model="wedding" /></dd>
        <dt>その他記念日</dt>
        <dd><OtherAnniversaries v-model="otherAnniversaries" /></dd>
        <dt>喫煙情報</dt>
        <dd>
          <RadioList :items="smokingMap" v-model.number="smoking" />
        </dd>
        <dt>アレルギー</dt>
        <dd><TextInput v-model="allergies" /></dd>
        <dt>好きな料理</dt>
        <dd><TextInput v-model="favoriteFood" /></dd>
        <dt>苦手な料理</dt>
        <dd><TextInput v-model="notFavoriteFood" /></dd>
        <dt>お客様メモ</dt>
        <dd><TextInput v-model="customerRemarks" /></dd>
      </dl>
      <dl class="cstm-note">
        <dt>Twitter</dt>
        <dd><TextInput v-model="twitter" placeholder="Twitterアカウント" /></dd>
        <dt>Facebook</dt>
        <dd><TextInput v-model="facebook" placeholder="Facebookアカウント" /></dd>
        <dt>DM可否</dt>
        <dd>
          <RadioList :items="['指定なし', '受け取る', '受け取らない']" v-model.number="directMail" />
        </dd>
      </dl>
      <dl v-if="currentStore.optionsSchema.customer" class="cstm-note">
        <template v-for="{ id, label, type, list } in currentStore.optionsSchema.customer">
          <dt :key="`label-${id}`" :class="{ checks: type === 5 }">{{ label }}</dt>
          <dd v-if="type === 5" :key="`value-${id}`" class="checks">
            <CheckBoxes
              class="checks-boxes"
              :items="omit(list, -1)"
              :checkedKeys="split(customer.options[id])"
              @change="updateOption(id, $event.join(','))"
            />
          </dd>
          <dd v-else :key="`value-${id}`">
            <TextInput :value="customer.options && customer.options[id]" @input="updateOption(id, $event)" />
          </dd>
        </template>
      </dl>
      <dl class="cstm-note">
        <dt>備考</dt>
        <dd><TextArea rows="4" v-model="remarks"></TextArea></dd>
        <dt>お客様関係者</dt>
        <dd><Button small @click="isShowRelationModal = true">関係者を選択</Button></dd>
        <dt></dt>
        <dd>
          <div class="select-wrap rel" v-for="(customer, idx) in this.customer.customerRelations" :key="idx">
            <TextInput class="wd--half" :value="customer.name" disabled />
            <Button class="delete btn-icon" small fit @click="deleteRelation(customer.id)"><DeleteIcon /></Button>
          </div>
        </dd>
      </dl>
    </div>
    <LoadingIndicator v-if="busy" />
    <CustomerSelectModal
      v-if="isShowRelationModal"
      title="お客様関係者を選択"
      :ignoreIds="[customer.id, ...customer.customerRelations.map(rel => rel.id)]"
      @select="selectRelation"
      @hide="isShowRelationModal = false"
    />
  </div>
</template>

<script>
import { omit } from 'lodash'
import { mapState, mapGetters } from 'vuex'
import { split } from '@/utils/string'
import { models } from '@/utils/vue'
import { format } from '@/utils/zipCode'
import Button from '@/components/atoms/Button'
import CheckBox from '@/components/atoms/CheckBox'
import RadioList from '@/components/atoms/RadioList'
import TextInput from '@/components/atoms/TextInput'
import TextArea from '@/components/atoms/TextArea'
import DeleteIcon from '@/components/icons/delete'
import CheckBoxes from '@/components/molecules/CheckBoxes'
import DateInput from '@/components/molecules/DateInput'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import TagInput from '@/components/molecules/TagInput'
import ToggleButton from '@/components/atoms/ToggleButton'
import NumericInput from '@/components/molecules/NumericInput'
import ImageMemo from '@/components/organisms/ImageMemo'
import { attributesMap } from '@/resources/customer/attributesMap'
import { genderMap } from '@/resources/customer/genderMap'
import { smokingMap } from '@/resources/customer/smokingMap'
import Navi from '../Navi'
import OtherAnniversaries from './OtherAnniversaries'
import CustomerSelectModal from '../modal/CustomerSelectModal'

/**
 * 顧客登録・編集
 */
export default {
  name: 'CustomerInput',
  components: {
    Button,
    CheckBox,
    LoadingIndicator,
    RadioList,
    TagInput,
    TextInput,
    TextArea,
    DeleteIcon,
    ToggleButton,
    CheckBoxes,
    DateInput,
    NumericInput,
    Navi,
    OtherAnniversaries,
    CustomerSelectModal,
    ImageMemo,
  },
  props: {
    customer: Object,
    busy: Boolean,
    allOtherAttributes: Array,
  },
  data() {
    return {
      isShowRelationModal: false,
    }
  },
  computed: {
    attributesMap: () => attributesMap,
    genderMap: () => genderMap,
    smokingMap: () => smokingMap,
    ...mapState({
      currentStore: state => state.stores.current.store,
      isSP: state => state.app.isSP,
    }),
    ...mapGetters({
      allImageMemos: 'imageMemos/all',
    }),
    isNew() {
      return !this.customer.id
    },
    ...models(
      [
        ...['lastName', 'lastNameKana', 'firstName', 'firstNameKana', 'tel1', 'tel2', 'mail1', 'mail2'],
        ...[
          'officeName',
          'department',
          'post',
          'officeZipCode',
          'officeAddress',
          'officeTel',
          'receiptAddress',
          'officeRemarks',
        ],
        ...['zipCode', 'address', 'twitter', 'facebook', 'directMail', 'remarks'],
        ...[
          'attributes',
          'otherAttributes',
          'gender',
          'birthday',
          'wedding',
          'otherAnniversaries',
          'smoking',
          'allergies',
          'favoriteFood',
          'notFavoriteFood',
          'customerRemarks',
        ],
      ],
      ({ customer }, key) => customer[key],
      ({ updateCustomer }, key, value) => updateCustomer({ [key]: value }),
    ),
  },
  methods: {
    omit,
    format,
    split,
    updateCustomer(customer) {
      this.$store.commit('customers/edit/update', customer)
    },
    updateOption(id, value) {
      this.updateCustomer({ options: { [id]: value } })
    },
    saveCustomer() {
      this.$emit('saveCustomer', this.isNew)
    },
    cancelEditCustomer() {
      this.$emit('cancelEditCustomer')
    },
    showEatInfoModal(rsvId) {
      this.$emit('showEatInfoModal', rsvId)
    },
    showRelationModal() {
      this.$emit('showRelationModal')
    },
    selectRelation({ id, name }) {
      this.updateCustomer({ customerRelations: this.customer.customerRelations.concat([{ id, name }]) })
      this.isShowRelationModal = false
    },
    deleteRelation(id) {
      this.updateCustomer({ customerRelations: this.customer.customerRelations.filter(val => val.id !== id) })
    },
    deleteCustomerImage(value) {
      this.$emit('deleteCustomerImage', value)
    },
  },
}
</script>

<style lang="postcss" scoped>
.customer-inout {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

  .responsive & {
    padding-bottom: 72px;
  }
}

.customer__book {
  box-sizing: border-box;
  flex: 1;
  -webkit-overflow-scrolling: touch;
  overflow-x: hidden;
  overflow-y: scroll;
  padding: 0 32px 16px;

  .responsive & {
    @media screen and (--sp-size) {
      padding: 0 16px 24px;

      .radio-list-2 {
        grid-auto-columns: 1fr 1fr 1fr;
        width: 100%;
      }
    }
  }
}

.responsive {
  @media screen and (--sp-size) {
    .customer__header {
      background: var(--white);
      border-bottom: none;
      border-top: 1px solid var(--border-color);
      bottom: 0;
      box-sizing: border-box;
      display: grid;
      grid-gap: 16px;
      grid-template-columns: 1fr 1fr;
      height: 72px;
      justify-content: center;
      left: 0;
      padding: 16px;
      position: fixed;
      width: 100%;
      z-index: 1000;

      >>> button {
        width: 100%;
      }
    }
  }
}

.input-text-wrap {
  width: 464px;
}

.checkbox-list {
  font-size: 15px;
  width: 310px;

  li:nth-child(n + 3) {
    margin-bottom: 0;
  }
}

.radio-list {
  font-size: 15px;
}

.cstm-note {
  align-items: flex-start;
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-wrap: wrap;
  margin-top: 20px;
  padding-top: 20px;
  position: relative;

  &:first-of-type {
    margin-top: 0;
  }

  dt {
    align-items: center;
    color: var(--gray);
    display: flex;
    font-size: 13px;
    font-weight: bold;
    height: 40px;
    line-height: 20px;
    width: 96px;

    &:last-of-type {
      height: auto;
    }
  }

  dd {
    font-size: 18px;
    margin-bottom: 8px;
    position: relative;
    width: calc(100% - 96px);

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  .sp-customer-attributes {
    display: none;
  }

  .checks {
    margin: 16px 0;
  }

  .checks-boxes {
    grid-auto-flow: row;
    grid-template-columns: 1fr 1fr;

    .sp & {
      grid-template-columns: auto;
    }
  }

  .responsive & {
    @media screen and (--sp-size) {
      display: block;

      dt,
      dd {
        width: 100%;
      }

      .input-text-wrap {
        display: grid;
        grid-auto-flow: column;
        grid-gap: 0 8px;
        width: 100%;
      }
      .wd--half {
        width: 100%;
      }

      .checks {
        margin: 0;
      }
    }

    .tb-customer-attributes {
      display: none;
    }

    .sp-customer-attributes {
      display: flex;
      flex-wrap: wrap;
      margin: -4px -4px 4px -4px;

      .attr {
        font-size: 15px;
        margin: 4px;
        overflow: hidden;
        width: auto;
      }
    }
  }
}

.select-wrap.rel {
  display: grid;
  grid-template-columns: max-content max-content;
  padding-bottom: 8px;
}

.btn-icon {
  height: 40px;
  overflow: hidden;
  position: relative;
  width: 40px;

  &::before {
    bottom: 0;
    content: '';
    height: 24px;
    left: 0;
    margin: auto;
    position: absolute;
    right: 0;
    top: 0;
    width: 24px;
  }

  &.delete {
    fill: var(--moderate-blue);
    margin-left: 16px;

    &::before {
      background: url(/assets/img/icons/btn_delete.png) no-repeat;
      background-size: 24px 48px;
    }

    &[disabled]::before {
      background: url(/assets/img/icons/btn_delete_disabled.png) no-repeat;
      background-size: 24px 24px;
    }
  }
}

.wd--half {
  width: 224px;
}

.other-attributes {
  border-top: 1px solid var(--border-color);
  margin: 8px 0 24px;
  padding-top: 16px;
}
</style>
